import React, { type ReactNode } from "react"
import { Navigate, type RouteObject, useLocation } from "react-router-dom"

import { NotAllowed } from "../../../../components/PermissionGate/NotAllowed.tsx"
import { PermissionGate } from "../../../../components/PermissionGate/PermissionGate.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  COMMUNICATION_BULK_SEND_PERMISSION,
  COMMUNICATION_SEND_PERMISSION,
  COMMUNICATION_SETTINGS_PERMISSION,
  COMMUNICATION_VIEW_ALL_PERMISSION
} from "../../../Communication/communication-permissions.ts"
import CommunicationLayout, {
  CommunicationHome
} from "../../../Communication/CommunicationLayout.tsx"
import { useOpensCommunicationTemplates } from "../../../Communication/hub/moved-redirects.tsx"
import { NoCommunicationAccess } from "../../../Communication/hub/NoCommunicationAccess.tsx"
import { lazyPage } from "../lazyPage.tsx"

const OverviewPage = lazyPage(
  () => import("../../../Communication/overview/OverviewPage.tsx")
)
const MessagesPage = lazyPage(
  () => import("../../../Communication/messages/MessagesPage.tsx")
)
const CampaignsPage = lazyPage(
  () => import("../../../Communication/campaigns/CampaignsPage.tsx")
)
const CampaignReportPage = lazyPage(
  () => import("../../../Communication/campaigns/CampaignReportPage.tsx")
)
const AutomationsPage = lazyPage(
  () => import("../../../Communication/automations/AutomationsPage.tsx")
)
const OptOutsPage = lazyPage(
  () => import("../../../Communication/consent/OptOutsPage.tsx")
)
const TemplatesPage = lazyPage(
  () => import("../../../Communication/templates/TemplatesTab.tsx")
)

const AUTOMATION_VIEW_PERMISSION =
  "settings.automations.settings_automation_view" as const
const AUTOMATION_MANAGE_PERMISSION =
  "settings.automations.settings_automation_manage" as const

// The library reads with Use or Manage message templates; sending alone does not.
const ReadsTemplates = ({ children }: { children: ReactNode }) =>
  useOpensCommunicationTemplates() ? <>{children}</> : <NotAllowed />

const readsMessages = (page: ReactNode) => (
  <PermissionGate
    mode="some"
    paths={[COMMUNICATION_SEND_PERMISSION, COMMUNICATION_VIEW_ALL_PERMISSION]}
  >
    {page}
  </PermissionGate>
)

const readsCampaigns = (page: ReactNode) => (
  <PermissionGate
    mode="some"
    paths={[
      COMMUNICATION_BULK_SEND_PERMISSION,
      COMMUNICATION_VIEW_ALL_PERMISSION
    ]}
  >
    {page}
  </PermissionGate>
)

const SETTINGS_REDIRECTS: Record<string, string> = {
  general: "general",
  email: "email",
  sms: "sms",
  "sending-rules": "rules"
}

// The old settings pages now open as a drawer over Overview; keep any query.
const SettingsRedirect = ({ section }: { section: string }) => {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.set("settings", section)
  return (
    <Navigate
      to={`${RoutesPaths.communication.overview}?${params.toString()}`}
      replace
    />
  )
}

export const communicationRoutes: RouteObject[] = [
  {
    path: RoutesPaths.communication.root,
    element: (
      <PermissionGate pages={["communication"]}>
        <CommunicationLayout />
      </PermissionGate>
    ),
    children: [
      {
        index: true,
        element: <CommunicationHome fallback={<NoCommunicationAccess />} />
      },
      {
        path: RoutesPaths.communication.overview,
        element: readsMessages(<OverviewPage />)
      },
      {
        path: RoutesPaths.communication.messages,
        element: readsMessages(<MessagesPage />)
      },
      {
        path: `${RoutesPaths.communication.messages}/:messageId`,
        element: readsMessages(<MessagesPage />)
      },
      {
        path: RoutesPaths.communication.campaigns,
        element: readsCampaigns(<CampaignsPage />)
      },
      {
        path: `${RoutesPaths.communication.campaigns}/:campaignId`,
        element: readsCampaigns(<CampaignReportPage />)
      },
      {
        path: RoutesPaths.communication.automations,
        element: (
          <PermissionGate
            mode="some"
            paths={[
              COMMUNICATION_SETTINGS_PERMISSION,
              AUTOMATION_VIEW_PERMISSION,
              AUTOMATION_MANAGE_PERMISSION
            ]}
          >
            <AutomationsPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.communication.templates,
        element: (
          <ReadsTemplates>
            <TemplatesPage />
          </ReadsTemplates>
        )
      },
      {
        path: RoutesPaths.communication.optOuts,
        element: (
          <PermissionGate paths={[COMMUNICATION_VIEW_ALL_PERMISSION]}>
            <OptOutsPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.communication.settingsTemplates,
        element: <Navigate to={RoutesPaths.communication.templates} replace />
      },
      ...Object.entries(SETTINGS_REDIRECTS).map(([path, section]) => ({
        path: `${RoutesPaths.communication.settings}/${path}`,
        element: <SettingsRedirect section={section} />
      })),
      {
        path: RoutesPaths.communication.settings,
        element: <SettingsRedirect section="general" />
      }
    ]
  }
]
